import type { FlightRefuel } from '../types';
import { expenseSplitLabel } from './expenseTypes';
import {
  fuelSplitFromSaved,
  fuelSplitRequest,
  refuelPurchaseTotal,
  type FuelSplitState,
} from './expenseSplit';

export const DEFAULT_FLIGHT_TYPE = 'По кругам';
export const ENGINE_RUN_TYPE = 'Гонка двигателя';

export const FLIGHT_TYPES = [
  'По кругам',
  'Маршрут',
  'Учебный',
  'Облёт',
  'Перегон',
  'Гонка двигателя',
  'Иное',
] as const;

export const FUEL_TYPES = ['100LL', 'Б-91/115', 'автобензин'] as const;

export type FuelType = (typeof FUEL_TYPES)[number];

export type FlightRefuelDraft = {
  key: string;
  liters: number;
  fuel_type: FuelType;
  price_per_liter: number;
  location: string;
} & FuelSplitState;

export const defaultLandingsForType = (type: string) =>
  type === ENGINE_RUN_TYPE ? 0 : 1;

export const parseLandings = (raw: string, type: string) => {
  const trimmed = String(raw ?? '').trim();
  if (trimmed === '') return defaultLandingsForType(type);
  const value = Number.parseInt(trimmed, 10);
  return Number.isFinite(value) && value >= 0
    ? value
    : defaultLandingsForType(type);
};

/** Моточасы / тахометр: пустое и мусор → null, не NaN. */
export const parseFlightNumber = (raw: string): number | null => {
  const trimmed = String(raw ?? '').trim().replace(',', '.');
  if (!trimmed || trimmed === '.' || trimmed === '-' || trimmed === '-.') {
    return null;
  }
  const value = Number(trimmed);
  return Number.isFinite(value) ? value : null;
};

export const refuelCost = (liters: number, price: number) =>
  Math.round(liters * price * 100) / 100;

export const formatRefuelLine = (
  item: Pick<
    FlightRefuel,
    'liters' | 'fuel_type' | 'price_per_liter' | 'location' | 'cost'
  > &
    Partial<Pick<FuelSplitState, 'costSplit'>> & {
      cost_split?: FlightRefuel['cost_split'];
    }
) => {
  const cost =
    item.cost != null
      ? item.cost
      : item.liters != null && item.price_per_liter != null
        ? refuelCost(Number(item.liters), Number(item.price_per_liter))
        : null;
  const split =
    cost != null && cost > 0
      ? expenseSplitLabel(item.costSplit || item.cost_split, 'fuel')
      : '';
  return [
    item.liters != null ? `${item.liters} л` : null,
    item.fuel_type || null,
    item.price_per_liter != null ? `${item.price_per_liter} ₽/л` : null,
    item.location || null,
    cost != null ? `${cost} ₽` : null,
    split || null,
  ]
    .filter(Boolean)
    .join(' · ');
};

export const toRefuelDrafts = (
  refuels: FlightRefuel[] | undefined,
  fallbackLocation: string,
  fallbackPayer = 0
): FlightRefuelDraft[] => {
  if (!refuels || refuels.length === 0) return [];
  return refuels.map((item, index) => ({
    key: String(item.id ?? `legacy-${index}`),
    liters: Number(item.liters) || 0,
    fuel_type: (FUEL_TYPES as readonly string[]).includes(item.fuel_type || '')
      ? (item.fuel_type as FuelType)
      : '100LL',
    price_per_liter: Number(item.price_per_liter) || 0,
    location: item.location || fallbackLocation,
    ...fuelSplitFromSaved(item, fallbackPayer),
  }));
};

export const refuelsPayload = (items: FlightRefuelDraft[]) =>
  items.map((item) => ({
    liters: item.liters,
    fuel_type: item.fuel_type,
    price_per_liter: item.price_per_liter,
    location: item.location.trim() || null,
    ...fuelSplitRequest(
      {
        costSplit: item.costSplit,
        manualShares: item.manualShares,
        flightIds: item.flightIds,
        payerUserId: item.payerUserId,
      },
      refuelPurchaseTotal([item]) > 0
    ),
  }));
